import { useCallback } from 'react';
import { useGeoMarkupStore } from '../store';
import { VIEW_MODE, type Coordinates } from '../types';
import { useArtifactNavigation } from './useArtifactNavigation';

/**
 * useTaskOperations Hook - Centralized Task Operations (SSOT)
 *
 * Custom hook that provides unified, consistent task operations for creating
 * and editing tasks. Navigates via overlay URLs (`/map/task/:id/edit`,
 * `/map/task/new`); store flags are driven by `useSyncOverlayWithUrl`.
 *
 * For navigating to task DETAILS (read-only view), use
 * `useTaskNavigation.navigateToTaskDetails` — it handles unsaved-changes
 * protection and tab-aware navigation.
 *
 * @example
 * ```tsx
 * const { openTaskForEdit, createTaskAt } = useTaskOperations();
 *
 * openTaskForEdit(task.id, task.geometry.calculatedCenter);
 * createTaskAt({ lat: 52.2297, lng: 21.0122 });
 * ```
 */
export const useTaskOperations = () => {
  // ===== STORE ACTIONS (guard reads + side effects) =====
  const zoomTo = useGeoMarkupStore((s) => s.map.zoomTo);
  const setFormAnchorPoint = useGeoMarkupStore(
    (s) => s.tasks.setFormAnchorPoint
  );

  // ===== URL-BASED NAVIGATION =====
  const { openArtifact } = useArtifactNavigation();

  /**
   * Open task for editing. Navigates to `/map/task/:id/edit`.
   *
   * Guard: If the form is already open in edit mode for the SAME task, this
   * function will only zoom to the coordinates without reopening the form.
   */
  const openTaskForEdit = useCallback(
    (taskId: string, coordinates: Coordinates) => {
      const currentState = useGeoMarkupStore.getState();
      const isAlreadyEditingSameTask =
        currentState.tasks.formIsOpen &&
        currentState.tasks.formMode === 'edit' &&
        (currentState.tasks.selectedId === taskId ||
          currentState.tasks.selectedTask?.shortId === taskId);

      if (isAlreadyEditingSameTask) {
        zoomTo(coordinates);
        return;
      }

      // Imperative callback outside React render — read URL directly because
      // `useSearchParams` isn't available here. Absent `?view=` means default.
      const currentView =
        new URLSearchParams(window.location.search).get('view') ??
        VIEW_MODE.MAP;
      if (currentView !== VIEW_MODE.MAP) {
        currentState.ui.openMapOverlay();
      }

      zoomTo(coordinates);
      openArtifact('tasks', 'edit', taskId);
    },
    [zoomTo, openArtifact]
  );

  /**
   * Create new task at location. Navigates to `/map/task/new` and sets
   * the anchor point in the store.
   */
  const createTaskAt = useCallback(
    (coordinates: Coordinates) => {
      setFormAnchorPoint(coordinates);
      zoomTo(coordinates);
      openArtifact('tasks', 'new');
    },
    [setFormAnchorPoint, zoomTo, openArtifact]
  );

  return {
    openTaskForEdit,
    createTaskAt,
  };
};
